// react
import React, { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

// mui
import { Tabs as MuiTabs, Tab as MuiTab, Box } from '@mui/material';

/**
 * Tabs is a component that allows users to navigate between different pages.
 * Tab state is synced to the URL query param `?tab=`.
 */
export const Tabs = ({
  tabs,
  defaultValue,
  value,
  onChange = () => {},
  isNoPadding = false,
}: TabsProps) => {
  // hooks
  const [searchParams, setSearchParams] = useSearchParams();
  const { search } = useLocation();

  const tabIds = tabs.map((t) => t.id);

  // state — initialize eagerly from URL (if it matches a known tab), then
  // defaultValue, then first tab
  const [val, setVal] = React.useState<string>(() => {
    const tab = searchParams.get('tab');
    if (tab && tabIds.includes(tab)) return tab;
    return defaultValue || tabs[0]?.id || '';
  });

  const currentValue = value || val;

  // Sync from URL changes — only accept ?tab= values that match a known tab
  // ID. This prevents the active tab from resetting during exit animations
  // (where the URL belongs to a different page) or when the param is absent.
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabIds.includes(tab)) {
      setVal(tab);
      onChange(tab);
    }
  }, [search]);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    searchParams.set('tab', newValue);
    setSearchParams(searchParams, { replace: true });
  };

  const activeTab = tabs.find((tab) => tab.id === currentValue);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <MuiTabs
          value={currentValue}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons={false}
        >
          {tabs.map((tab) => (
            <MuiTab key={tab.id} label={tab.label} value={tab.id} />
          ))}
        </MuiTabs>
      </Box>
      {activeTab && (
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            ...(!isNoPadding && { pt: 3 }),
            overflow: activeTab.isScroll ? 'auto' : 'visible',
          }}
        >
          {activeTab.content}
        </Box>
      )}
    </Box>
  );
};

export interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  isScroll?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  isNoPadding?: boolean;
}

Tabs.displayName = 'Tabs';

export default Tabs;
